import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { downloadExcel } from '../../utils/excelExport';

const READINESS_LABELS = { in_progress: 'В работе', completed: 'Завершена', on_hold: 'Приостановлена', cancelled: 'Отменена' };
const STATUS_LABELS = { new: 'Новая', pending: 'На подписи', paid: 'Оплачена', rejected: 'Отклонена' };
const money = (n) => `${Math.round(Number(n || 0)).toLocaleString('ru')} ₽`;
const toInputDate = (d) => d ? String(d).slice(0, 10) : '';

function DealEditForm({ deal, onClose, onSaved }) {
  const [name, setName] = useState(deal.name);
  const [managerName, setManagerName] = useState(deal.manager_name || '');
  const [customerName, setCustomerName] = useState(deal.customer_name || '');
  const [city, setCity] = useState(deal.city || '');
  const [clientSource, setClientSource] = useState(deal.client_source || '');
  const [contractingEntity, setContractingEntity] = useState(deal.contracting_entity || '');
  const [budget, setBudget] = useState(deal.budget || 0);
  const [readinessStatus, setReadinessStatus] = useState(deal.readiness_status || 'in_progress');
  const [prepaymentDate, setPrepaymentDate] = useState(toInputDate(deal.prepayment_date));
  const [paymentDate, setPaymentDate] = useState(toInputDate(deal.payment_date));
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Укажите название проекта'); return; }
    setSaving(true);
    try {
      await api.patch(`/finance/deals/${deal.id}`, {
        name: name.trim(),
        manager_name: managerName.trim() || null,
        customer_name: customerName.trim() || null,
        city: city.trim() || null,
        client_source: clientSource.trim() || null,
        contracting_entity: contractingEntity.trim() || null,
        budget: parseFloat(budget) || 0,
        readiness_status: readinessStatus,
        prepayment_date: prepaymentDate || null,
        payment_date: paymentDate || null,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка сохранения');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h2>Изменить проект</h2><button className="modal-close" onClick={onClose}>×</button></div>
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group"><label>Название проекта *</label><input value={name} onChange={e => setName(e.target.value)} required /></div>
          <div className="form-row">
            <div className="form-group"><label>Менеджер</label><input value={managerName} onChange={e => setManagerName(e.target.value)} /></div>
            <div className="form-group"><label>Заказчик</label><input value={customerName} onChange={e => setCustomerName(e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Город</label><input value={city} onChange={e => setCity(e.target.value)} /></div>
            <div className="form-group"><label>Источник</label><input value={clientSource} onChange={e => setClientSource(e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Юр.лицо</label><input value={contractingEntity} onChange={e => setContractingEntity(e.target.value)} /></div>
            <div className="form-group"><label>Бюджет, ₽</label><input type="number" step="0.01" value={budget} onChange={e => setBudget(e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Дата предоплаты</label><input type="date" value={prepaymentDate} onChange={e => setPrepaymentDate(e.target.value)} /></div>
            <div className="form-group"><label>Дата оплаты</label><input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} /></div>
          </div>
          <div className="form-group">
            <label>Статус проекта</label>
            <select value={readinessStatus} onChange={e => setReadinessStatus(e.target.value)}>
              {Object.entries(READINESS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteDealModal({ deal, onClose, onDeleted }) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!comment.trim()) { setError('Укажите причину удаления'); return; }
    setSaving(true);
    try {
      await api.delete(`/finance/deals/${deal.id}`, { data: { comment: comment.trim() } });
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка удаления');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header"><h2>Удалить проект</h2><button className="modal-close" onClick={onClose}>×</button></div>
        <p className="text-muted mb-2">
          Проект «{deal.name}» будет удалён безвозвратно. Кто и когда удалил — с указанной причиной —
          останется в журнале удалений (доступен админам со страницы «Проекты»).
        </p>
        {(deal.entries.length > 0 || deal.payment_requests.length > 0 || deal.design_tasks_count > 0) && (
          <div className="error-msg mb-2">
            {deal.entries.length > 0 && <div>Записей ДДС по проекту: {deal.entries.length} — отвяжутся от проекта, сами записи не удалятся.</div>}
            {deal.payment_requests.length > 0 && <div>Заявок на оплату: {deal.payment_requests.length} — отвяжутся от проекта, сами заявки не удалятся.</div>}
            {deal.design_tasks_count > 0 && <div>Задач дизайнеров: {deal.design_tasks_count} — удалятся вместе с проектом, включая начисления ЗП по ним.</div>}
          </div>
        )}
        {error && <div className="error-msg">{error}</div>}
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Причина удаления *</label>
            <textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder="Например: дубль проекта, ошибочно заведён" required />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-danger" disabled={saving}>{saving ? 'Удаление...' : 'Удалить проект'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// onClose — если карточка открыта поверх списка (CEO-дашборд), а не отдельной страницей:
// тогда id приходит пропсом, а вместо «← К проектам» показывается «Закрыть».
export default function DealDetail({ id: idProp, onClose }) {
  const params = useParams();
  const id = idProp ?? params.id;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => { setLoading(true); api.get(`/finance/deals/${id}`).then(r => setData(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="loading-text">Загрузка...</div>;
  if (!data) return <div className="empty-state">Не найдено</div>;

  const canEdit = user.role === 'admin' || data.created_by === user.id;
  // Отклонённые заявки в потенциальные расходы не входят — их оплачивать уже не будут.
  const activeRequests = data.payment_requests.filter(p => p.status !== 'rejected');
  const requestsTotal = activeRequests.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const requestsPaid = activeRequests.filter(p => p.status === 'paid').reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const canExport = ['admin', 'accountant', 'rop'].includes(user.role);

  const exportEntries = () => downloadExcel(
    `ДДС по проекту — ${data.name}`,
    ['Дата', 'Тип', 'Статья', 'Сумма', 'Контрагент'],
    data.entries.map(e => [
      new Date(e.entry_date).toLocaleDateString('ru'),
      e.operation_type ? e.operation_type[0].toUpperCase() + e.operation_type.slice(1) : (e.direction === 'income' ? 'Приход' : 'Расход'),
      e.article_name || '—', money(e.amount), e.contractor_name || '—',
    ])
  );
  const exportRequests = () => downloadExcel(
    `Заявки по проекту — ${data.name}`,
    ['#', 'Статья', 'Сумма', 'Статус'],
    data.payment_requests.map(p => [p.id, p.article, money(p.amount), STATUS_LABELS[p.status]])
  );

  return (
    <div>
      <div className="toolbar">
        {onClose
          ? <button className="btn-secondary btn-sm" onClick={onClose}>← Закрыть</button>
          : <Link to="/projects" className="btn-secondary btn-sm">← К проектам</Link>}
        <h2 style={{ marginLeft: 8 }}>{data.name}</h2>
        <span className="badge badge-new">{READINESS_LABELS[data.readiness_status] || data.readiness_status}</span>
        <div className="toolbar-spacer" />
        {canEdit && <button className="btn-secondary btn-sm" onClick={() => setEditing(true)}>Изменить</button>}
        {user.role === 'admin' && <button className="btn-danger btn-sm" onClick={() => setDeleting(true)}>Удалить</button>}
      </div>

      <div className="stats-row">
        <div className="stat-card"><div className="stat-value">{money(data.budget)}</div><div className="stat-label">Бюджет</div></div>
        <div className="stat-card"><div className="stat-value">{money(data.paid)}</div><div className="stat-label">Оплачено заказчиком</div></div>
        <div className="stat-card"><div className="stat-value">{money(data.remainder)}</div><div className="stat-label">Неоплаченная часть бюджета</div></div>
        <div className="stat-card"><div className="stat-value">{money(data.actual_expenses)}</div><div className="stat-label">Подтверждённые оплаты</div></div>
        <div className="stat-card">
          <div className="stat-value">{money(requestsTotal)}</div>
          <div className="stat-label">Все заявки (потенциальные расходы)</div>
          <div className="stat-label">из них оплачено: {money(requestsPaid)}</div>
        </div>
        <div className="stat-card"><div className="stat-value">{money(data.designer_salary)}</div><div className="stat-label">ЗП дизайнеров</div></div>
        <div className="stat-card" style={{ borderColor: data.margin >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          <div className="stat-value" style={{ color: data.margin >= 0 ? 'var(--success)' : 'var(--danger)' }}>{money(data.margin)}</div>
          <div className="stat-label">Денежный результат</div>
        </div>
      </div>

      <div className="card mb-2">
        <div className="form-row">
          <div><span className="text-muted">№ договора: </span>{data.contract_number || '—'}</div>
          <div><span className="text-muted">Дата договора: </span>{data.contract_date ? new Date(data.contract_date).toLocaleDateString('ru') : '—'}</div>
        </div>
        <div className="form-row">
          <div><span className="text-muted">Заказчик: </span>{data.customer_name || '—'}</div>
          <div><span className="text-muted">Город: </span>{data.city || '—'}</div>
        </div>
        <div className="form-row">
          <div><span className="text-muted">Источник клиента: </span>{data.client_source || '—'}</div>
          <div><span className="text-muted">Юр.лицо: </span>{data.contracting_entity || '—'}</div>
        </div>
        <div className="form-row">
          <div><span className="text-muted">Дата предоплаты: </span>{data.prepayment_date ? new Date(data.prepayment_date).toLocaleDateString('ru') : '—'}</div>
          <div><span className="text-muted">Дата оплаты: </span>{data.payment_date ? new Date(data.payment_date).toLocaleDateString('ru') : '—'}</div>
        </div>
        <div><span className="text-muted">Создал: </span>{data.creator_name || '—'}</div>
      </div>

      <div className="toolbar" style={{ marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>Записи ДДС по проекту</h3>
        <div className="toolbar-spacer" />
        {canExport && data.entries.length > 0 && <button className="btn-secondary btn-sm" onClick={exportEntries}>Экспорт в Excel</button>}
      </div>
      <div className="table-wrap mb-2">
        <table>
          <thead><tr><th>Дата</th><th>Тип</th><th>Статья</th><th>Сумма</th><th>Контрагент</th></tr></thead>
          <tbody>
            {data.entries.length === 0 ? <tr><td colSpan={5} className="empty-state">Нет записей</td></tr> : data.entries.map(e => (
              <tr key={e.id}>
                <td>{new Date(e.entry_date).toLocaleDateString('ru')}</td>
                <td><span className={`badge ${e.direction === 'income' ? 'badge-paid' : 'badge-new'}`}>{e.operation_type ? e.operation_type[0].toUpperCase() + e.operation_type.slice(1) : (e.direction === 'income' ? 'Приход' : 'Расход')}</span></td>
                <td>{e.article_name || '—'}</td>
                <td>{money(e.amount)}</td>
                <td>{e.contractor_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="toolbar" style={{ marginBottom: 4 }}>
        <h3 style={{ margin: 0 }}>Заявки на оплату по проекту</h3>
        {activeRequests.length > 0 && (
          <span className="text-muted" style={{ marginLeft: 12 }}>
            оплачено <b>{money(requestsPaid)}</b> из <b>{money(requestsTotal)}</b>
          </span>
        )}
        <div className="toolbar-spacer" />
        {canExport && data.payment_requests.length > 0 && <button className="btn-secondary btn-sm" onClick={exportRequests}>Экспорт в Excel</button>}
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>#</th><th>Статья</th><th>Сумма</th><th>Статус</th><th>Вложения</th></tr></thead>
          <tbody>
            {data.payment_requests.length === 0 ? <tr><td colSpan={5} className="empty-state">Нет заявок</td></tr> : data.payment_requests.map(p => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.article}</td>
                <td>{money(p.amount)}</td>
                <td><span className={`badge badge-${p.status}`}>{STATUS_LABELS[p.status]}</span></td>
                <td>
                  {!p.attachments || p.attachments.length === 0 ? <span className="text-muted">—</span> : p.attachments.map(a => (
                    <div key={a.id}>
                      <a href={a.url} target="_blank" rel="noreferrer">{a.kind === 'requisites' ? '📎 Реквизиты' : '📎 Счёт/чек'}</a>
                    </div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <DealEditForm deal={data} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); load(); }} />}
      {deleting && <DeleteDealModal deal={data} onClose={() => setDeleting(false)} onDeleted={() => (onClose ? onClose() : navigate('/projects'))} />}
    </div>
  );
}
